import { useState, useCallback, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Breadcrumbs,
  Link,
  IconButton,
  Tooltip,
  CircularProgress,
  Skeleton,
  Button,
  Menu,
  MenuItem,
  ListItemSecondaryAction,
  Snackbar,
  Alert,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RefreshIcon from "@mui/icons-material/Refresh";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import AddToPhotosIcon from "@mui/icons-material/AddToPhotos";
import axios from "axios";

import {
  useWorkspaceFiles,
  useDeleteWorkspaceFile,
  useUploadWorkspaceFile,
  downloadWorkspaceFile,
  WorkspaceEntry,
} from "./WorkspaceServices";
import { getFileIcon, formatFileSize } from "./fileUtils";
import { usePermission } from "src/helpers/Auth";
import { toastApiError } from "src/reusable/utils/toastApiError";
import { useAddFromWorkspace } from "src/api/projectFolders/projectFolderService";
import MoveDialog, {
  writabilityReason,
} from "src/views/assistant/projects/MoveDialog";
import ReplaceFileDialog, {
  PromoteConflict,
} from "src/views/assistant/projects/components/ReplaceFileDialog";
import { captureEvent, AA_EVENTS } from "src/config/posthog";

/** Extract just the last segment of a path (basename). */
function basename(name: string): string {
  const trimmed = name.replace(/\/+$/, "");
  const idx = trimmed.lastIndexOf("/");
  return idx >= 0 ? trimmed.slice(idx + 1) : trimmed;
}

const NO_WRITE_PERMISSION_MSG =
  "You do not have permission to modify workspace files";

const NO_PROMOTE_PERMISSION_MSG =
  "You do not have permission to add files to a project";

interface WorkspaceFileExplorerProps {
  compact?: boolean;
}

export default function WorkspaceFileExplorer({
  compact = false,
}: WorkspaceFileExplorerProps) {
  const location = useLocation();
  const [currentPath, setCurrentPath] = useState(
    () => new URLSearchParams(location.search).get("path") || "",
  );
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);

  // Sync with URL ?path= param when it changes (e.g. deep link from email records)
  useEffect(() => {
    const urlPath = new URLSearchParams(location.search).get("path") || "";
    if (urlPath !== currentPath) {
      setCurrentPath(urlPath);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuEntry, setMenuEntry] = useState<WorkspaceEntry | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, isError, refetch } = useWorkspaceFiles(currentPath);
  const deleteMutation = useDeleteWorkspaceFile();
  const uploadMutation = useUploadWorkspaceFile();

  // Upload/delete are gated server-side on model:write; promote ("Add to
  // Project") targets a project folder and is gated on project:write. Mirror
  // each here so users without permission see disabled actions instead of
  // silent 403s.
  const { canIt: canWrite } = usePermission("model:write");
  const { canIt: canPromote } = usePermission("project:write");

  // Promote ("Add to Project", spec §5.9): copies the workspace file into a
  // writable project folder; the workspace original stays put.
  const [promoteEntry, setPromoteEntry] = useState<WorkspaceEntry | null>(null);
  const [promoteSuccess, setPromoteSuccess] = useState<string | null>(null);
  const [promoteError, setPromoteError] = useState<string | null>(null);
  const [promoteConflict, setPromoteConflict] =
    useState<PromoteConflict | null>(null);
  const promoteMutation = useAddFromWorkspace();

  const closePromoteDialog = useCallback(() => {
    setPromoteEntry(null);
    promoteMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmPromote = (destination: { id: number; name: string }) => {
    if (!promoteEntry) return;
    const entry = promoteEntry;
    const requestData = {
      source: "workspace_file" as const,
      workspace_path: entry.name,
    };
    promoteMutation.mutate(
      {
        folderId: destination.id,
        data: requestData,
      },
      {
        onSuccess: (res) => {
          captureEvent(AA_EVENTS.FOLDER_FILE_ADDED, {
            folder_id: destination.id,
            source: "workspace_file",
            deconflicted: res.deconflicted ?? false,
          });
          closePromoteDialog();
          if (res.deconflicted && res.original_name) {
            // Name collision: landed as a "(N)" copy — offer in-place
            // replacement of the original (ReplaceFileDialog below).
            setPromoteConflict({
              folderId: destination.id,
              folderName: `"${destination.name}"`,
              originalName: res.original_name,
              deconflictedName: res.name,
              request: requestData,
            });
          } else {
            setPromoteSuccess(`Added "${res.name}" to "${destination.name}".`);
          }
        },
      },
    );
  };

  const promoteErrorMessage = promoteMutation.isError
    ? axios.isAxiosError(promoteMutation.error) &&
      typeof promoteMutation.error.response?.data?.detail === "string"
      ? promoteMutation.error.response.data.detail
      : "Failed to add the file to the project. Please try again."
    : null;

  // Navigation — entry.name is already the full workspace-relative path
  const navigateToFolder = useCallback((fullPath: string) => {
    setCurrentPath(fullPath.replace(/\/+$/, ""));
  }, []);

  const navigateToPath = useCallback((path: string) => {
    setCurrentPath(path);
  }, []);

  const pathSegments = currentPath ? currentPath.split("/") : [];

  // Download — entry.name is already the full workspace-relative path
  const handleDownload = useCallback(async (entry: WorkspaceEntry) => {
    setDownloadingFile(entry.name);
    try {
      await downloadWorkspaceFile(entry.name);
    } catch (err) {
      console.error("Download failed:", err);
      toastApiError(err, `Failed to download ${basename(entry.name)}`);
    } finally {
      setDownloadingFile(null);
    }
  }, []);

  // Delete — entry.name is already the full workspace-relative path
  const handleDelete = useCallback(
    async (entry: WorkspaceEntry) => {
      try {
        await deleteMutation.mutateAsync(entry.name);
      } catch (err) {
        console.error("Delete failed:", err);
        toastApiError(err, `Failed to delete ${basename(entry.name)}`);
      }
    },
    [deleteMutation],
  );

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files?.length) return;
      for (const file of Array.from(files)) {
        try {
          await uploadMutation.mutateAsync({ path: currentPath, file });
        } catch (err) {
          console.error("Upload failed:", err);
          toastApiError(err, `Failed to upload ${file.name}`);
        }
      }
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [currentPath, uploadMutation],
  );

  const handleMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>, entry: WorkspaceEntry) => {
      event.stopPropagation();
      setMenuAnchor(event.currentTarget);
      setMenuEntry(entry);
    },
    [],
  );

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
    setMenuEntry(null);
  }, []);

  // Combine directories and files, directories first
  const entries: WorkspaceEntry[] = [
    ...(data?.directories ?? []),
    ...(data?.files ?? []),
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Toolbar: breadcrumbs + actions */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: compact ? 1.5 : 2,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
          minHeight: 44,
        }}
      >
        <Breadcrumbs
          maxItems={compact ? 3 : 6}
          sx={{
            flex: 1,
            minWidth: 0,
            "& .MuiBreadcrumbs-li": { minWidth: 0 },
          }}
        >
          <Link
            component="button"
            underline="hover"
            color={currentPath ? "inherit" : "text.primary"}
            onClick={() => navigateToPath("")}
            data-attr="assistant-workspace:link:navigate-to-root"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              fontSize: "0.813rem",
            }}
          >
            <HomeIcon sx={{ fontSize: 16 }} />
            {!compact && "workspace"}
          </Link>
          {pathSegments.map((segment, i) => {
            const segmentPath = pathSegments.slice(0, i + 1).join("/");
            const isLast = i === pathSegments.length - 1;
            return (
              <Link
                key={segmentPath}
                component="button"
                underline={isLast ? "none" : "hover"}
                color={isLast ? "text.primary" : "inherit"}
                onClick={() => navigateToPath(segmentPath)}
                data-attr={`assistant-workspace:link:navigate-to-${segment}`}
                sx={{
                  fontSize: "0.813rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {segment}
              </Link>
            );
          })}
        </Breadcrumbs>

        <Tooltip title={canWrite ? "Upload file" : NO_WRITE_PERMISSION_MSG}>
          <span>
            {/* The span wrapper (needed so the tooltip fires on a disabled
                button) absorbs the Tooltip-derived aria-label, so the button
                must carry its own accessible name. */}
            <IconButton
              size="small"
              onClick={handleUploadClick}
              disabled={!canWrite}
              aria-label="Upload file"
              data-testid="aw-upload-file-btn"
              data-attr="assistant-workspace:btn:upload-file"
            >
              <UploadFileIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Refresh">
          <IconButton
            size="small"
            onClick={() => refetch()}
            data-attr="assistant-workspace:btn:refresh-files"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={handleFileChange}
        />
      </Box>

      {/* Uploading indicator */}
      {uploadMutation.isLoading && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 0.5,
            bgcolor: "info.main",
            color: "info.contrastText",
          }}
        >
          <CircularProgress size={14} color="inherit" />
          <Typography variant="caption">Uploading...</Typography>
        </Box>
      )}

      {/* Info banner — only show at workspace root, not inside subfolders */}
      {!currentPath && !compact && (
        <Box
          sx={{
            px: 2,
            py: 1,
            bgcolor: "action.hover",
            borderBottom: 1,
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Your agent workspace is shared across all conversations. Files
            created by the agent in any chat appear here and persist between
            sessions.
          </Typography>
        </Box>
      )}

      {/* Content area */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {isLoading ? (
          <Box sx={{ p: compact ? 1 : 2 }}>
            {[...Array(5)].map((_, i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={compact ? 36 : 48}
                sx={{ mb: 0.5, borderRadius: 1 }}
              />
            ))}
          </Box>
        ) : isError ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography color="error" variant="body2" gutterBottom>
              Failed to load workspace files.
            </Typography>
            <Button
              size="small"
              onClick={() => refetch()}
              data-attr="assistant-workspace:btn:retry-load-files"
            >
              Retry
            </Button>
          </Box>
        ) : entries.length === 0 ? (
          <Box
            sx={{
              p: 4,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
              color: "text.secondary",
            }}
          >
            {currentPath ? (
              <>
                <FolderOpenIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                <Typography variant="body2">This folder is empty.</Typography>
              </>
            ) : (
              <>
                <CreateNewFolderIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                <Typography variant="body2">
                  No files yet. Your agent will create files here as it works.
                </Typography>
                <Tooltip title={canWrite ? "" : NO_WRITE_PERMISSION_MSG}>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<UploadFileIcon />}
                      onClick={handleUploadClick}
                      disabled={!canWrite}
                      sx={{ mt: 1 }}
                      data-testid="aw-upload-file-empty-state-btn"
                      data-attr="assistant-workspace:btn:upload-file-empty-state"
                    >
                      Upload a file
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
          </Box>
        ) : (
          <List dense={compact} disablePadding>
            {entries.map((entry) => {
              const isDir = entry.type === "directory";
              const displayName = basename(entry.name);
              const Icon = getFileIcon(displayName, isDir);
              const isDownloading = downloadingFile === entry.name;

              return (
                <ListItemButton
                  key={`${entry.type}-${entry.name}`}
                  onClick={() =>
                    isDir ? navigateToFolder(entry.name) : handleDownload(entry)
                  }
                  data-attr={`assistant-workspace:btn:${isDir ? "open-folder" : "download-file"}-${displayName}`}
                  sx={{
                    px: compact ? 1.5 : 2,
                    py: compact ? 0.5 : 0.75,
                    "&:hover .action-buttons": { opacity: 1 },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: compact ? 32 : 40 }}>
                    {isDownloading ? (
                      <CircularProgress size={compact ? 18 : 22} />
                    ) : (
                      <Icon
                        fontSize="small"
                        color={isDir ? "primary" : "action"}
                      />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={displayName}
                    secondary={
                      !isDir
                        ? [
                            formatFileSize(entry.size),
                            entry.last_modified
                              ? new Date(
                                  entry.last_modified,
                                ).toLocaleDateString()
                              : null,
                          ]
                            .filter(Boolean)
                            .join(" \u00B7 ")
                        : undefined
                    }
                    primaryTypographyProps={{
                      variant: compact ? "body2" : "body1",
                      noWrap: true,
                      fontWeight: isDir ? 500 : 400,
                    }}
                    secondaryTypographyProps={{
                      variant: "caption",
                      noWrap: true,
                    }}
                  />
                  {!isDir && (
                    <ListItemSecondaryAction
                      className="action-buttons"
                      sx={{
                        opacity: { xs: 1, md: 0 },
                        transition: "opacity 0.15s",
                      }}
                    >
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => handleMenuOpen(e, entry)}
                        aria-label={`File options for ${displayName}`}
                        data-testid={`workspace-file-options-${displayName}`}
                        data-attr={`assistant-workspace:btn:file-options-${displayName}`}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  )}
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>

      {/* Context menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        slotProps={{ paper: { sx: { minWidth: 160 } } }}
      >
        <MenuItem
          onClick={() => {
            // pointerEvents:auto on the inner Box (for the tooltip) also lets a
            // click through the disabled item, so guard the action here.
            if (!canPromote) return;
            if (menuEntry) setPromoteEntry(menuEntry);
            handleMenuClose();
          }}
          disabled={!canPromote}
          data-testid="workspace-menu-add-to-project"
          data-attr="assistant-workspace:menu:add-to-project"
        >
          <Tooltip
            title={canPromote ? "" : NO_PROMOTE_PERMISSION_MSG}
            placement="left"
          >
            {/* pointerEvents:auto re-enables hover: a disabled MenuItem
                (ButtonBase) sets pointer-events:none, which would suppress the
                tooltip explaining why the item is greyed out. */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                pointerEvents: "auto",
              }}
            >
              <ListItemIcon>
                <AddToPhotosIcon fontSize="small" />
              </ListItemIcon>
              Add to Project
            </Box>
          </Tooltip>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuEntry) handleDownload(menuEntry);
            handleMenuClose();
          }}
          data-attr="assistant-workspace:menu:download-file"
        >
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          Download
        </MenuItem>
        <MenuItem
          onClick={() => {
            // pointerEvents:auto on the inner Box (for the tooltip) also lets a
            // click through the disabled item, so guard the action here.
            if (!canWrite) return;
            if (menuEntry) handleDelete(menuEntry);
            handleMenuClose();
          }}
          disabled={!canWrite}
          sx={{ color: "error.main" }}
          data-testid="aw-delete-file-menu-item"
          data-attr="assistant-workspace:menu:delete-file"
        >
          <Tooltip
            title={canWrite ? "" : NO_WRITE_PERMISSION_MSG}
            placement="left"
          >
            {/* pointerEvents:auto re-enables hover on the disabled ButtonBase
                so the permission tooltip still renders. */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                pointerEvents: "auto",
              }}
            >
              <ListItemIcon>
                <DeleteOutlineIcon fontSize="small" color="error" />
              </ListItemIcon>
              Delete
            </Box>
          </Tooltip>
        </MenuItem>
      </Menu>

      {promoteEntry !== null && (
        <MoveDialog
          open
          onClose={closePromoteDialog}
          title={`Add "${basename(promoteEntry.name)}" to a project`}
          disabledReason={writabilityReason}
          onMove={confirmPromote}
          isMoving={promoteMutation.isLoading}
          error={promoteErrorMessage}
          confirmLabel="Add"
        />
      )}
      <ReplaceFileDialog
        conflict={promoteConflict}
        onKeepBoth={() => {
          if (promoteConflict) {
            setPromoteSuccess(
              `Added "${promoteConflict.deconflictedName}" to ${promoteConflict.folderName}.`,
            );
          }
          setPromoteConflict(null);
        }}
        onReplaced={(resolved, cleanupWarning) => {
          setPromoteSuccess(
            `Replaced "${resolved.originalName}" in ${resolved.folderName}` +
              (cleanupWarning ? ` — but ${cleanupWarning}.` : "."),
          );
          setPromoteConflict(null);
        }}
        onError={(msg) => {
          setPromoteError(msg);
          setPromoteConflict(null);
        }}
      />
      <Snackbar
        open={Boolean(promoteSuccess)}
        autoHideDuration={5000}
        onClose={() => setPromoteSuccess(null)}
        message={promoteSuccess}
        // Bottom-center clears the side panels; the default bottom-left tucks
        // this behind the workspace/right sidebar (matches FolderContentsList).
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
      <Snackbar
        open={Boolean(promoteError)}
        autoHideDuration={6000}
        onClose={() => setPromoteError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setPromoteError(null)}>
          {promoteError}
        </Alert>
      </Snackbar>
    </Box>
  );
}
